import React from 'react'
import { IconSeekers } from 'ui-seeker/core'
import { formatPercentage, currencyInfoByCode, formatPrice } from 'ui-seeker/utils'
import CountUp from "react-countup"
import { useTranslation } from 'react-i18next'
import { AppTooltip } from 'app/@components'
import { IMAGE } from 'app/@common/constants'
import { formatIntegerPrice, integerPriceCountUpProps } from 'app/@utils/price'

// integerPrices: Competition, Parity and Packages analyzers show money without decimals (CU-869fmuqwb);
// BI reuses this card and keeps the decimals (prop off by default).
const moneyFormatter = integerPrices => integerPrices ? formatIntegerPrice : formatPrice

// Render alternativo del delta cuando se pasa previousInfo. Mismo formato que el delta
// inline de las celdas (AppComparisonDelta): caret + ±X€ + (±X%) con tooltip "PREVIOUS PRICE / fecha".
//
// `isPercentage`/`higherIsBetter` (BI de Parity, 03/09/2026): Packages (único consumidor hasta
// ahora) son siempre precios donde bajar es bueno — de ahí el `formatPrice()` fijo y
// `sign < 0 → diff-success` fijo. Para métricas tipo Índice de paridad (puntos 0-100, subir es
// bueno) o Diferencia media (%, sin ser dinero) esos dos supuestos son incorrectos por defecto
// desactivados (`false`), así que Packages sigue exactamente igual que antes.
function PreviousDeltaLine({ previousInfo, currency, isPercentage=false, higherIsBetter=false, integerPrices=false }) {
    const formatMoney = moneyFormatter(integerPrices)
    const { t } = useTranslation('common')
    const { abs_diff, perc_diff, date, sign } = previousInfo
    const isGood = higherIsBetter ? sign > 0 : sign < 0
    const signChar = sign < 0 ? '-' : '+'

    const titleContent = <div style={{ fontSize: '11px', fontWeight: 700, lineHeight: 1.3 }}>
        {t('vsPreviousChip.previous')}{date ? ` - ${date}` : ''}
    </div>

    return <AppTooltip title={titleContent}>
        <span
            className={`app-card__price-panel-diff ${isGood ? 'diff-success' : 'diff-error'}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
            <IconSeekers
                className="app-card__price-panel-diff-icon"
                icon={sign < 0 ? 'caret-up' : 'caret-down'}
                iconType="fontAwesome"
                iconWeight="fas"
            />
            <span className="app-card__price-panel-diff-value">
                {signChar}{isPercentage ? `${Math.abs(abs_diff).toFixed(2)} pts` : formatMoney(Math.abs(abs_diff), { currency })}
            </span>
            <span style={{ opacity: 0.85 }}>
                ({signChar}{Math.abs(perc_diff).toFixed(1)}%)
            </span>
        </span>
    </AppTooltip>
}

function BySiteDeltaLine({ previousInfo, currency, integerPrices=false }) {
    const formatMoney = moneyFormatter(integerPrices)
    const { t } = useTranslation('common')
    const { abs_diff, perc_diff, date, sign } = previousInfo
    const lower = sign < 0
    const signChar = sign < 0 ? '-' : '+'
    const titleContent = <div style={{ fontSize: '11px', fontWeight: 700, lineHeight: 1.3 }}>
        {t('vsPreviousChip.previous')}{date ? ` - ${date}` : ''}
    </div>
    return <AppTooltip title={titleContent}>
        <span className={`app-card__by-site-diff ${lower ? 'diff-success' : 'diff-error'}`}>
            <IconSeekers
                className="app-card__by-site-diff-icon"
                icon={lower ? 'caret-up' : 'caret-down'}
                iconType="fontAwesome"
                iconWeight="fas"
            />
            <span>{signChar}{formatMoney(Math.abs(abs_diff), { currency })}</span>
            <span style={{ opacity: 0.85 }}>({signChar}{Math.abs(perc_diff).toFixed(1)}%)</span>
        </span>
    </AppTooltip>
}

function BySitePanel({ items, currency, integerPrices=false }) {
    const formatMoney = moneyFormatter(integerPrices)
    // Grid layout: each site contributes exactly 4 direct children (icon, name,
    // value, diff-or-placeholder) to keep prices vertically aligned across rows.
    // No row wrapper — AppTooltip wraps with an extra <div>, which would break
    // `display: contents` and shift the grid cells. Using React.Fragment.
    return <div className="app-card__by-site">
        {items.map(s => {
            const slug = String(s.site_name || '').split('.')[0]
            const hasPrev = s.previousInfo && typeof s.previousInfo.abs_diff === 'number' && s.previousInfo.abs_diff !== 0
            return <React.Fragment key={s.id_site}>
                <img
                    className="app-card__by-site-icon"
                    src={`${IMAGE.ASSETS}/${slug}-mini.png`}
                    alt={s.site_name}
                    onError={i => i.target.src = `${IMAGE.ASSETS}/web.png`}
                />
                <span className="app-card__by-site-name">{s.site_name}</span>
                <span className="app-card__by-site-value">
                    {!!s.value ? formatMoney(s.value, { currency }) : formatMoney(0)}
                </span>
                {hasPrev
                    ? <BySiteDeltaLine previousInfo={s.previousInfo} currency={currency} integerPrices={integerPrices} />
                    : <span className="app-card__by-site-diff-placeholder" />}
            </React.Fragment>
        })}
    </div>
}

// isRecalculating: the value is being refetched (e.g. compset after an exclusion change) → the price panel pulses
export default function AppCard({ data, isMainHotelCard= false, currency = null, isPercentage = false, higherIsBetter = false, showDiffValue = true, tooltip = '', integerPrices = false, isRecalculating = false }) {
    const { t } = useTranslation('common')
    const { label, diff, value, previousInfo, by_site } = data
    const currencyConfig = integerPrices
        ? integerPriceCountUpProps(currency)
        : (currency ? currencyInfoByCode(currency) : {})
    const diffClass = !!diff ? (diff < 0 ? 'diff-success' : 'diff-error') : ''
    // previousInfo viene de useCards cuando el toggle "vs Previous" está activo.
    // Si está presente, sustituye el render del diff porcentaje suelto por la línea estilo delta.
    const showPreviousDelta = previousInfo && typeof previousInfo.abs_diff === 'number' && previousInfo.abs_diff !== 0

    // by_site solo lo envía Packages tras Fase 5. Si el caller no lo pasa, la card
    // renderiza como antes (Competition/Parity no se ven afectados).
    const hasBySite = Array.isArray(by_site)
    const showBySiteLateral = hasBySite && by_site.length > 1

    const formatValue = () => {
        if (isPercentage) return !!value ? <CountUp end={value} suffix="%" /> : formatPrice(value)
        return !!value ? <CountUp end={value} {...currencyConfig} /> : formatPrice(value)
    }

    const content = (
        <div className="app-card__text-panel h-full flex flex-col">
            <div className="app-card__text-panel-header">
                <p className="app-card__text-panel-label">{label}</p>
                {!!tooltip && <IconSeekers style={{fontSize: '15px'}} icon="info-circle" iconType="fontAwesome"/>}
            </div>
        </div>
    )

    const mainColumn = <>
        {tooltip ? (
            <AppTooltip title={tooltip} placement="right">
                {content}
            </AppTooltip>
        ) : content}
        <div className={`app-card__price-panel flex-1 flex flex-col justify-center ${isRecalculating ? 'compset-recalculating' : ''}`}>
            <span className="app-card__price-panel-value">
                {!hasBySite && <p className="app-card__text-panel-subtitle">{t('metrics.averagePrice')}</p>}
                {formatValue()}
                {showPreviousDelta && <PreviousDeltaLine previousInfo={previousInfo} currency={currency} isPercentage={isPercentage} higherIsBetter={higherIsBetter} integerPrices={integerPrices} />}
                {!showPreviousDelta && !!diff && (
                    <span className={`app-card__price-panel-diff ${diffClass}`}>
                        {showDiffValue && (
                            <span className="app-card__price-panel-diff-value">
                                {formatPercentage(diff, { decimals: 2 })}
                            </span>
                        )}
                        <IconSeekers
                            className="app-card__price-panel-diff-icon"
                            icon={diff < 0 ? 'arrow-down' : 'arrow-up'}
                            iconType="fontAwesome"
                            iconWeight='fa'
                        />
                    </span>
                )}
            </span>
            {hasBySite && <p className="app-card__min-subtitle">{t('metrics.averageMinSitePrice')}</p>}
        </div>
    </>

    const outerClass = `app-card ${isMainHotelCard && diff > 0 ? 'app-card--error' : ''} ${hasBySite ? 'app-card--with-sites' : ''}`

    if (!hasBySite) {
        return <div style={{ height: '95px' }} className={outerClass}>
            {mainColumn}
        </div>
    }

    return <div className={outerClass}>
        <div className="app-card__main">
            {mainColumn}
        </div>
        {showBySiteLateral && <span className="app-card__by-site-label">{t('metrics.averageSite')}</span>}
        {showBySiteLateral && <BySitePanel items={by_site} currency={currency} integerPrices={integerPrices} />}
    </div>
}
